import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  Star,
  Shield,
  Zap,
  Clock,
  ArrowRight,
  Loader2,
  X,
  AlertTriangle,
  MapPin,
  Facebook,
  Instagram,
  BookOpen,
  Maximize2,
  Copy,
  MessageCircle,
  ChevronDown,
  Users,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";


const TIMER_KEY = (slug) => `promo_timer_${slug}`;
const SCARCITY_KEY = (slug) => `promo_scarcity_${slug}`;

function getCountdown(slug) {
  const stored = localStorage.getItem(TIMER_KEY(slug));
  if (stored) {
    const diff = parseInt(stored, 10) - Date.now();
    if (diff > 0) return diff;
  }
  const end = Date.now() + 72 * 60 * 60 * 1000;
  localStorage.setItem(TIMER_KEY(slug), String(end));
  return 72 * 60 * 60 * 1000;
}

function getScarcity(slug) {
  const stored = localStorage.getItem(SCARCITY_KEY(slug));
  if (stored) return parseInt(stored, 10);
  const n = Math.floor(Math.random() * 36) + 12;
  localStorage.setItem(SCARCITY_KEY(slug), String(n));
  return n;
}

function CountdownTimer({ slug, urgencyText }) {
  const [remaining, setRemaining] = useState(() => getCountdown(slug));
  useEffect(() => {
    const id = setInterval(() => {
      const left = getCountdown(slug);
      setRemaining(left);
      if (left <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [slug]);
  if (remaining <= 0)
    return <span className="text-blue-500 font-black">PROMO HABIS.</span>;
  const d = Math.floor(remaining / 86400000);
  const h = Math.floor((remaining % 86400000) / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return (
    <span>
      {urgencyText}{" "}
      <span className="font-mono font-black text-white bg-[#0A0F1E]/40 px-2 py-0.5 rounded text-lg tracking-wider">
        {d > 0 ? `${d}d ` : ""}
        {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:
        <span className="animate-pulse">{String(s).padStart(2, "0")}</span>
      </span>
    </span>
  );
}

function rupiah(n) {
  return "Rp" + Number(n).toLocaleString("id-ID");
}

// Meta Pixel helper — base pixel di-init di /public/index.html (ID 993045030025532)
function fbqTrack(event, params = {}) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try {
      window.fbq("track", event, params);
    } catch (e) {
      // swallow — tracking tidak boleh ganggu UX
    }
  }
}

const TABS = [
  {
    id: "ss1",
    icon: "MapPin",
    badge: "GOOGLE MAPS",
    title: "Cari 10.000+ Bisnis dari Google Maps — 30 Detik",
    desc: "Masukkan keyword + lokasi. RESADA akan scrape ribuan bisnis real-time dari Google Maps. Gak perlu scroll manual 12 jam. Tinggal klik, kopi, selesai.",
    benefits: [
      "Scrape 10.000+ hasil dalam 30-120 detik",
      "Filter rating, review count, kategori, lokasi",
      "Data fresh real-time, bukan database basi",
      "Export langsung atau save ke CRM internal",
    ],
  },
  {
    id: "ss2",
    icon: "MapPin",
    badge: "HASIL SCRAPE",
    title: "Ratusan Leads Langsung Muncul — Siap Dikirimi WA",
    desc: "Setelah scrape selesai, semua lead langsung muncul di tabel. Nomor HP, website, email, alamat, rating, review — lengkap. Tinggal centang, pilih template, blast.",
    benefits: [
      "Tabel interaktif dengan filter per kolom",
      "Sort by rating, kota, kategori, status WA",
      "Bulk actions: tag, status, enrich AI, cek WA",
      "Export CSV kapan saja",
    ],
  },
  {
    id: "ss3",
    icon: "MapPin",
    badge: "DETAIL LEAD",
    title: "Klik Satu Lead — Lihat Semua Data + Analisa AI",
    desc: "Klik lead mana pun untuk lihat detail lengkap: info bisnis, mapping, description AI, segmentasi pasar, skala bisnis, produk/layanan. Tau segalanya sebelum kirim WA.",
    benefits: [
      "Data lengkap: phone, email, website, alamat, rating",
      "AI enrichment: description, segment, scale, products",
      "Lead scoring: hot/warm/cold otomatis",
      "History interaksi — tau sudah di-WA atau belum",
    ],
  },
  {
    id: "ss4",
    icon: "BookOpen",
    badge: "COMMUNITY TIPS",
    title: "Database + Edukasi — Gak Cuma Scrape Doang",
    desc: "RESADA bukan cuma tool scraping. Ada section Community Tips khusus untuk anggota: strategi closing, cara nulis WA yang dibales, tips rebut klien dari kompetitor. Isinya gak ada di blog umum.",
    benefits: [
      "Artikel eksklusif khusus pengguna RESADA",
      "Tips closing, copywriting WA, strategi sales",
      "Updated mingguan — konten baru tiap minggu",
      "Akses penuh dengan akun RESADA",
    ],
  },
  {
    id: "ssfb1",
    icon: "Facebook",
    badge: "FACEBOOK SCRAPER",
    title: "Scrape Bisnis dari Facebook — Dapet Leads Hangat",
    desc: "Facebook penuh bisnis yang advertise. Gak semua orang tau cara scrape dari FB. RESADA bisa — ambil nama bisnis, kontak, kategori, lokasi langsung dari Facebook Pages.",
    benefits: [
      "Scrape bisnis dari Facebook Pages & Marketplace",
      "Dapet leads yang gak ada di Google Maps",
      "Khusus untuk target B2B — banyak FB Page bisnis",
      "Data: nama, kontak, kategori, followers",
    ],
  },
  {
    id: "ssig1",
    icon: "Instagram",
    badge: "INSTAGRAM SCRAPER",
    title: "Instagram Juga Bisa — Leads dari Bio & Post",
    desc: "Bisnis kecil banyak promo di Instagram. Scrape IG langsung: ambil data bisnis dari bio, post, comment. Cocok buat target UMKM, reseller, dropshipper.",
    benefits: [
      "Scrape data bisnis dari profil IG",
      "Kontak dari bio + email di post",
      "Cocok untuk reseller & dropshipper hunting",
      "Data: username, bio, email, followers, kategori",
    ],
  },
];

function StickyBottomBar({ onBuy }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden
                    bg-blue-700 border-t border-blue-600 
                    px-4 py-3 flex items-center justify-between
                    shadow-lg">
      <div>
        <p className="text-white font-black text-sm">Rp89.000</p>
        <p className="text-blue-200 text-xs line-through">Rp599.000</p>
      </div>
      <button
        onClick={onBuy}
        className="bg-white text-blue-700 font-black text-sm 
                   px-5 py-2.5 rounded-xl active:scale-95 
                   transition flex items-center gap-2"
      >
        ⚡ BELI SEKARANG
      </button>
    </div>
  );
}

export default function PromoPage() {
  const { slug } = useParams();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("ss1");
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const videoRef = useRef(null);
  const scarcity = useMemo(() => getScarcity(slug), [slug]);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const activeTabData = useMemo(() => TABS.find((t) => t.id === activeTab), [activeTab]);
  
  // Progress bar state
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Social proof notification state
  const [showSocialProof, setShowSocialProof] = useState(false);
  const [currentProof, setCurrentProof] = useState(null);

  // Fake social proof data - 200+ realistic entries (censored for privacy)
  const socialProofData = [
    { name: "M**d R**i", email: "mu**ki23@gmail.com", city: "Jakarta", time: "2 menit" },
    { name: "S**i N**a", email: "si**za88@gmail.com", city: "Bandung", time: "5 menit" },
    { name: "A**d F**i", email: "ah**zi45@yahoo.com", city: "Surabaya", time: "3 menit" },
    { name: "D**i S**a", email: "de**ka12@gmail.com", city: "Yogyakarta", time: "7 menit" },
    { name: "B**i S**o", email: "bu**so77@outlook.com", city: "Semarang", time: "4 menit" },
    { name: "R**a K**a", email: "ri**ma34@gmail.com", city: "Malang", time: "6 menit" },
    { name: "A**s P**o", email: "ag**yo56@yahoo.com", city: "Denpasar", time: "8 menit" },
    { name: "F**i H**i", email: "fi**ni91@gmail.com", city: "Medan", time: "1 menit" },
    { name: "D**i S**n", email: "do**an22@hotmail.com", city: "Makassar", time: "12 menit" },
    { name: "L**a P**a", email: "li**ta67@gmail.com", city: "Palembang", time: "15 menit" },
    { name: "E**o W**a", email: "ek**ya43@yahoo.com", city: "Tangerang", time: "9 menit" },
    { name: "M**a S**i", email: "ma**ri18@gmail.com", city: "Depok", time: "11 menit" },
    { name: "R**a F**h", email: "re**ah29@outlook.com", city: "Bekasi", time: "14 menit" },
    { name: "N**a M**a", email: "ni**na55@gmail.com", city: "Bogor", time: "6 menit" },
    { name: "H**o G**n", email: "he**an71@yahoo.com", city: "Jakarta", time: "18 menit" },
    { name: "S**i R**u", email: "sa**yu84@gmail.com", city: "Bandung", time: "3 menit" },
    { name: "B**g S**a", email: "ba**ya36@hotmail.com", city: "Surabaya", time: "21 menit" },
    { name: "W**n D**i", email: "wu**ri92@gmail.com", city: "Solo", time: "5 menit" },
    { name: "F**r R**n", email: "fa**an47@yahoo.com", city: "Malang", time: "27 menit" },
    { name: "I**h L**i", email: "in**ri63@gmail.com", city: "Yogyakarta", time: "8 menit" },
    { name: "T**o S**o", email: "to**to19@outlook.com", city: "Semarang", time: "13 menit" },
    { name: "R**a S**i", email: "ra**ri78@gmail.com", city: "Pontianak", time: "16 menit" },
    { name: "Y**i H**o", email: "yu**no24@yahoo.com", city: "Balikpapan", time: "4 menit" },
    { name: "P**i U**i", email: "pu**mi51@gmail.com", city: "Samarinda", time: "22 menit" },
    { name: "A**i W**a", email: "an**ya89@hotmail.com", city: "Manado", time: "7 menit" },
    { name: "L**a M**a", email: "li**na37@gmail.com", city: "Pekanbaru", time: "10 menit" },
    { name: "D**i S**i", email: "de**di66@yahoo.com", city: "Batam", time: "19 menit" },
    { name: "S**i W**i", email: "sr**ni42@gmail.com", city: "Jambi", time: "2 menit" },
    { name: "I**n K**a", email: "ir**ma75@outlook.com", city: "Padang", time: "25 menit" },
    { name: "A**i S**i", email: "an**ti93@gmail.com", city: "Bandar Lampung", time: "6 menit" },
    { name: "H**a S**a", email: "he**ra28@yahoo.com", city: "Jakarta", time: "1 jam" },
    { name: "M**a P**a", email: "me**ta54@gmail.com", city: "Bandung", time: "11 menit" },
    { name: "R**n A**d", email: "ri**ad81@hotmail.com", city: "Surabaya", time: "17 menit" },
    { name: "L**a A**a", email: "li**ia39@gmail.com", city: "Cirebon", time: "9 menit" },
    { name: "J**o S**o", email: "jo**to62@yahoo.com", city: "Tegal", time: "23 menit" },
    { name: "D**a P**a", email: "di**ma46@gmail.com", city: "Purwokerto", time: "5 menit" },
    { name: "A**i W**o", email: "ar**wo87@outlook.com", city: "Pekalongan", time: "14 menit" },
    { name: "T**i H**i", email: "tu**ni31@gmail.com", city: "Cilacap", time: "20 menit" },
    { name: "B**y K**n", email: "be**an69@yahoo.com", city: "Kudus", time: "3 menit" },
    { name: "V**a N**a", email: "ve**ta95@gmail.com", city: "Jepara", time: "26 menit" },
    { name: "A**n P**a", email: "an**ya52@hotmail.com", city: "Magelang", time: "8 menit" },
    { name: "Y**i A**i", email: "yu**ti74@gmail.com", city: "Salatiga", time: "12 menit" },
    { name: "R**i H**n", email: "ru**an41@yahoo.com", city: "Surakarta", time: "18 menit" },
    { name: "S**a D**i", email: "si**wi86@gmail.com", city: "Madiun", time: "4 menit" },
    { name: "W**u H**t", email: "wa**at33@outlook.com", city: "Kediri", time: "21 menit" },
    { name: "N**a S**i", email: "ni**ri68@gmail.com", city: "Blitar", time: "7 menit" },
    { name: "E**o P**o", email: "ek**wo25@yahoo.com", city: "Mojokerto", time: "15 menit" },
    { name: "L**s S**i", email: "li**ni94@gmail.com", city: "Pasuruan", time: "2 menit" },
    { name: "B**g I**n", email: "ba**an58@hotmail.com", city: "Probolinggo", time: "28 menit" },
    { name: "I**a P**i", email: "ik**wi72@gmail.com", city: "Banyuwangi", time: "10 menit" },
    { name: "D**y G**n", email: "do**an44@yahoo.com", city: "Jember", time: "16 menit" },
    { name: "R**a M**a", email: "ri**na83@gmail.com", city: "Situbondo", time: "5 menit" },
    { name: "A**g N**o", email: "ag**ho27@outlook.com", city: "Bondowoso", time: "22 menit" },
    { name: "D**i L**i", email: "dw**ri61@gmail.com", city: "Lumajang", time: "9 menit" },
    { name: "H**i S**o", email: "ha**lo49@yahoo.com", city: "Tulungagung", time: "19 menit" },
    { name: "E**i Y**a", email: "er**na96@gmail.com", city: "Trenggalek", time: "3 menit" },
    { name: "W**n S**n", email: "wa**an35@hotmail.com", city: "Nganjuk", time: "24 menit" },
    { name: "Y**k R**i", email: "ya**ti79@gmail.com", city: "Bojonegoro", time: "6 menit" },
    { name: "S**a D**a", email: "su**ma53@yahoo.com", city: "Tuban", time: "13 menit" },
    { name: "L**a W**a", email: "li**ya88@gmail.com", city: "Lamongan", time: "17 menit" },
    { name: "J**a K**a", email: "ja**ma26@outlook.com", city: "Gresik", time: "1 jam" },
    { name: "S**a W**i", email: "si**ri64@gmail.com", city: "Sidoarjo", time: "11 menit" },
    { name: "R**i S**a", email: "ro**ra42@yahoo.com", city: "Mojokerto", time: "8 menit" },
    { name: "T**a P**i", email: "ti**ri91@gmail.com", city: "Bangkalan", time: "20 menit" },
    { name: "D**n P**i", email: "di**ti57@hotmail.com", city: "Sampang", time: "4 menit" },
    { name: "B**u A**i", email: "ba**ji73@gmail.com", city: "Pamekasan", time: "25 menit" },
    { name: "F**i A**u", email: "fi**yu38@yahoo.com", city: "Sumenep", time: "7 menit" },
    { name: "O**i S**n", email: "ok**an85@gmail.com", city: "Denpasar", time: "14 menit" },
    { name: "N**a K**a", email: "ni**ia59@outlook.com", city: "Gianyar", time: "2 menit" },
    { name: "Y**o P**o", email: "ya**yo21@gmail.com", city: "Tabanan", time: "18 menit" },
    { name: "W**i S**i", email: "wa**ti76@yahoo.com", city: "Karangasem", time: "5 menit" },
    { name: "H**i W**o", email: "ha**to48@hotmail.com", city: "Klungkung", time: "23 menit" },
    { name: "R**h P**a", email: "ra**ta92@gmail.com", city: "Buleleng", time: "9 menit" },
    { name: "T**h S**a", email: "te**sa34@yahoo.com", city: "Jembrana", time: "16 menit" },
    { name: "M**a K**i", email: "ma**ti67@gmail.com", city: "Bangli", time: "3 menit" },
    { name: "F**i R**n", email: "fi**an45@outlook.com", city: "Mataram", time: "21 menit" },
    { name: "D**i R**i", email: "de**ri89@gmail.com", city: "Bima", time: "6 menit" },
    { name: "Y**a P**a", email: "yo**ma52@yahoo.com", city: "Dompu", time: "27 menit" },
    { name: "L**a N**a", email: "li**za71@gmail.com", city: "Sumbawa", time: "10 menit" },
    { name: "A**i S**a", email: "an**ra36@hotmail.com", city: "Kupang", time: "13 menit" },
    { name: "N**l F**h", email: "nu**ah94@gmail.com", city: "Ende", time: "19 menit" },
    { name: "R**n H**t", email: "ri**at63@yahoo.com", city: "Maumere", time: "4 menit" },
    { name: "S**a A**a", email: "si**ia28@gmail.com", city: "Ruteng", time: "22 menit" },
    { name: "I**n M**a", email: "ir**na81@outlook.com", city: "Waingapu", time: "8 menit" },
    { name: "R**o W**i", email: "re**ri55@gmail.com", city: "Pontianak", time: "15 menit" },
    { name: "A**t K**n", email: "ad**an47@yahoo.com", city: "Singkawang", time: "2 menit" },
    { name: "F**i P**a", email: "fe**ma93@gmail.com", city: "Mempawah", time: "26 menit" },
    { name: "R**l A**d", email: "ri**ad39@hotmail.com", city: "Ketapang", time: "7 menit" },
    { name: "M**i S**i", email: "me**ri66@gmail.com", city: "Sintang", time: "12 menit" },
    { name: "A**e N**a", email: "ad**ha24@yahoo.com", city: "Palangkaraya", time: "17 menit" },
    { name: "V**a O**i", email: "vi**ni87@gmail.com", city: "Sampit", time: "5 menit" },
    { name: "I**n S**o", email: "im**so51@outlook.com", city: "Kuala Kapuas", time: "20 menit" },
    { name: "D**h A**u", email: "di**yu75@gmail.com", city: "Banjarmasin", time: "9 menit" },
    { name: "F**y G**n", email: "fe**an43@yahoo.com", city: "Banjarbaru", time: "23 menit" },
    { name: "L**a S**i", email: "la**ri98@gmail.com", city: "Martapura", time: "3 menit" },
    { name: "A**f B**n", email: "ar**an32@hotmail.com", city: "Balikpapan", time: "18 menit" },
    { name: "R**a P**a", email: "ri**ta69@gmail.com", city: "Samarinda", time: "6 menit" },
    { name: "D**y S**n", email: "de**an56@yahoo.com", city: "Bontang", time: "24 menit" },
    { name: "A**u L**i", email: "ay**ri84@gmail.com", city: "Tarakan", time: "11 menit" },
    { name: "T**k H**t", email: "ta**at41@outlook.com", city: "Berau", time: "14 menit" },
    { name: "M**a R**i", email: "mi**ti77@gmail.com", city: "Kutai", time: "1 jam" },
    { name: "S**t P**o", email: "si**wo29@yahoo.com", city: "Manado", time: "8 menit" },
    { name: "N**a A**a", email: "ni**ia95@gmail.com", city: "Bitung", time: "19 menit" },
    { name: "A**i N**o", email: "aj**ho54@hotmail.com", city: "Tomohon", time: "4 menit" },
    { name: "R**i S**i", email: "ri**ti72@gmail.com", city: "Kotamobagu", time: "21 menit" },
    { name: "A**i S**n", email: "ar**an37@yahoo.com", city: "Minahasa", time: "7 menit" },
    { name: "B**a K**a", email: "be**ma91@gmail.com", city: "Palu", time: "25 menit" },
    { name: "G**g P**a", email: "gi**ma64@outlook.com", city: "Toli-Toli", time: "10 menit" },
    { name: "C**a D**i", email: "ci**wi46@gmail.com", city: "Luwuk", time: "15 menit" },
    { name: "R**a A**a", email: "ra**ya83@yahoo.com", city: "Poso", time: "2 menit" },
    { name: "T**i W**i", email: "ta**ri58@gmail.com", city: "Makassar", time: "28 menit" },
    { name: "K**n A**a", email: "ke**ra22@hotmail.com", city: "Pare-Pare", time: "6 menit" },
    { name: "S**a U**i", email: "sa**mi79@gmail.com", city: "Palopo", time: "13 menit" },
    { name: "H**s M**a", email: "ha**na35@yahoo.com", city: "Watampone", time: "17 menit" },
    { name: "G**a P**a", email: "gi**ta96@gmail.com", city: "Bulukumba", time: "5 menit" },
    { name: "W**y K**n", email: "wi**an61@outlook.com", city: "Kendari", time: "22 menit" },
    { name: "V**i R**u", email: "vi**yu44@gmail.com", city: "Baubau", time: "9 menit" },
    { name: "A**a S**a", email: "an**ra88@yahoo.com", city: "Kolaka", time: "16 menit" },
    { name: "I**n P**a", email: "in**ta53@gmail.com", city: "Raha", time: "3 menit" },
    { name: "D**s P**o", email: "di**yo27@hotmail.com", city: "Gorontalo", time: "20 menit" },
    { name: "N**a S**i", email: "ni**ri76@gmail.com", city: "Limboto", time: "7 menit" },
    { name: "G**h N**a", email: "ga**ha42@yahoo.com", city: "Mamuju", time: "24 menit" },
    { name: "S**i W**i", email: "sa**ri99@gmail.com", city: "Majene", time: "11 menit" },
    { name: "A**r H**t", email: "an**at65@outlook.com", city: "Ambon", time: "14 menit" },
    { name: "K**a S**i", email: "ka**ri31@gmail.com", city: "Tual", time: "1 jam" },
    { name: "O**i K**n", email: "ok**an86@yahoo.com", city: "Masohi", time: "8 menit" },
    { name: "L**a M**a", email: "li**na59@gmail.com", city: "Ternate", time: "19 menit" },
    { name: "D**i S**i", email: "de**di23@hotmail.com", city: "Tidore", time: "4 menit" },
    { name: "S**i R**u", email: "sa**yu92@gmail.com", city: "Sofifi", time: "21 menit" },
    { name: "H**a W**a", email: "he**ya57@yahoo.com", city: "Jayapura", time: "7 menit" },
    { name: "M**a U**i", email: "me**mi74@gmail.com", city: "Biak", time: "25 menit" },
    { name: "Y**i P**o", email: "yu**no48@outlook.com", city: "Merauke", time: "10 menit" },
    { name: "L**a P**a", email: "li**ta91@gmail.com", city: "Timika", time: "15 menit" },
    { name: "R**n S**a", email: "ri**ra36@yahoo.com", city: "Nabire", time: "2 menit" },
    { name: "D**a M**a", email: "di**na69@gmail.com", city: "Sorong", time: "28 menit" },
    { name: "E**o S**o", email: "ek**so43@hotmail.com", city: "Manokwari", time: "6 menit" },
    { name: "P**i A**a", email: "pu**ia82@gmail.com", city: "Fakfak", time: "13 menit" },
    { name: "R**a A**a", email: "re**ya55@yahoo.com", city: "Wamena", time: "17 menit" },
    { name: "N**a R**i", email: "ni**ti28@gmail.com", city: "Jakarta Pusat", time: "5 menit" },
    { name: "H**i K**n", email: "ha**an97@outlook.com", city: "Jakarta Selatan", time: "22 menit" },
    { name: "M**a S**i", email: "ma**ri64@gmail.com", city: "Jakarta Timur", time: "9 menit" },
    { name: "A**i P**a", email: "an**ya39@yahoo.com", city: "Jakarta Barat", time: "16 menit" },
    { name: "W**n S**i", email: "wu**ri81@gmail.com", city: "Jakarta Utara", time: "3 menit" },
    { name: "B**i H**n", email: "bu**an52@hotmail.com", city: "Tangerang Selatan", time: "20 menit" },
    { name: "R**a O**i", email: "ri**ni76@gmail.com", city: "Cikarang", time: "7 menit" },
    { name: "A**i N**o", email: "ad**ho44@yahoo.com", city: "Karawang", time: "24 menit" },
    { name: "L**a P**a", email: "li**ta93@gmail.com", city: "Purwakarta", time: "11 menit" },
    { name: "Y**a S**n", email: "yo**an67@outlook.com", city: "Subang", time: "14 menit" },
    { name: "D**i R**u", email: "de**yu32@gmail.com", city: "Indramayu", time: "1 jam" },
    { name: "R**n H**t", email: "ri**at85@yahoo.com", city: "Kuningan", time: "8 menit" },
    { name: "S**a M**a", email: "si**na58@gmail.com", city: "Majalengka", time: "19 menit" },
    { name: "E**o W**a", email: "ek**ya21@hotmail.com", city: "Tasikmalaya", time: "4 menit" },
    { name: "N**a K**a", email: "ni**ma94@gmail.com", city: "Garut", time: "21 menit" },
    { name: "A**f S**o", email: "ar**so66@yahoo.com", city: "Ciamis", time: "7 menit" },
    { name: "T**a W**i", email: "ti**ri41@gmail.com", city: "Banjar", time: "25 menit" },
    { name: "J**o P**o", email: "jo**no87@outlook.com", city: "Pangandaran", time: "10 menit" },
    { name: "V**a S**i", email: "vi**ri53@gmail.com", city: "Sukabumi", time: "15 menit" },
    { name: "D**i S**i", email: "do**di79@yahoo.com", city: "Cianjur", time: "2 menit" },
    { name: "R**a P**a", email: "ri**ta46@gmail.com", city: "Bandung Barat", time: "28 menit" },
    { name: "H**a G**n", email: "he**an72@hotmail.com", city: "Cimahi", time: "6 menit" },
    { name: "L**a A**a", email: "li**ia95@gmail.com", city: "Sumedang", time: "13 menit" },
    { name: "R**i A**a", email: "ri**ya38@yahoo.com", city: "Bandung", time: "17 menit" },
    { name: "D**i L**i", email: "de**ri63@gmail.com", city: "Cirebon", time: "5 menit" },
    { name: "A**i K**n", email: "ar**an89@outlook.com", city: "Bekasi", time: "22 menit" },
    { name: "M**a P**a", email: "me**ta54@gmail.com", city: "Depok", time: "9 menit" },
    { name: "Y**o P**o", email: "ya**yo27@yahoo.com", city: "Bogor", time: "16 menit" },
    { name: "S**i R**i", email: "sa**ti71@gmail.com", city: "Sukabumi", time: "3 menit" },
    { name: "A**i S**n", email: "an**an45@hotmail.com", city: "Tangerang", time: "20 menit" },
    { name: "N**a M**a", email: "ni**na98@gmail.com", city: "Serang", time: "7 menit" },
    { name: "R**i H**n", email: "ru**an62@yahoo.com", city: "Cilegon", time: "24 menit" },
    { name: "L**a K**a", email: "li**ma34@gmail.com", city: "Lebak", time: "11 menit" },
    { name: "B**u N**o", email: "ba**ho86@outlook.com", city: "Pandeglang", time: "14 menit" },
    { name: "T**i S**i", email: "ta**ri59@gmail.com", city: "Rangkasbitung", time: "1 jam" },
  ];

  // Scroll progress handler
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Social proof notification handler
  useEffect(() => {
    if (result) return; // Don't show if already purchased
    
    const showNotification = () => {
      const randomProof = socialProofData[Math.floor(Math.random() * socialProofData.length)];
      setCurrentProof(randomProof);
      setShowSocialProof(true);
      
      // Hide after 5 seconds
      setTimeout(() => {
        setShowSocialProof(false);
      }, 5000);
    };
    
    // First notification after 8 seconds
    const firstTimer = setTimeout(showNotification, 8000);
    
    // Subsequent notifications every 20-35 seconds
    const interval = setInterval(() => {
      const randomDelay = Math.random() * 15000 + 20000; // 20-35 seconds
      setTimeout(showNotification, randomDelay);
    }, 35000);
    
    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [result]);

  // Exit-intent detection
  useEffect(() => {
    const handler = (e) => {
      if (e.clientY < 5 && !showExitIntent && !result) setShowExitIntent(true);
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [showExitIntent, result]);

  const copyPassword = () => {
    if (result?.generated_password) {
      navigator.clipboard.writeText(result.generated_password);
      setCopied(true);
      toast.success("Password disalin!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const loadVideo = () => {
    if (videoLoaded || videoLoading || videoUrl) return;
    setVideoLoading(true);
    setVideoProgress(0);

    // Fetch the video as a blob — only starts downloading when user clicks
    fetch("/image/sda/demo/demonew.mp4")
      .then((response) => {
        const contentLength = response.headers.get("Content-Length");
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        let loaded = 0;

        const reader = response.body.getReader();
        const chunks = [];

        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) {
              const blob = new Blob(chunks, { type: "video/mp4" });
              const url = URL.createObjectURL(blob);
              setVideoUrl(url);
              setVideoLoading(false);
              setVideoLoaded(true);
              // Auto-play after load
              setTimeout(() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(() => {});
                }
              }, 100);
              return;
            }
            chunks.push(value);
            loaded += value.length;
            if (total) {
              setVideoProgress(Math.round((loaded / total) * 100));
            } else {
              setVideoProgress((prev) => Math.min(prev + 3, 90));
            }
            return pump();
          });
        }
        return pump();
      })
      .catch(() => {
        // Fallback: use direct src
        setVideoUrl("/image/sda/demo/demonew.mp4");
        setVideoLoading(false);
        setVideoLoaded(true);
      });
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/promo/config/${slug}`)
      .then((r) => {
        setCfg(r.data);
        // Meta Pixel: ViewContent saat halaman promo siap
        fbqTrack("ViewContent", {
          content_name: r.data?.meta_title || slug,
          content_category: "promo",
          content_ids: [slug],
          value: r.data?.price || 89000,
          currency: "IDR",
        });
      })
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Meta Pixel: InitiateCheckout saat modal checkout dibuka
  useEffect(() => {
    if (showCheckout && cfg) {
      fbqTrack("InitiateCheckout", {
        content_name: cfg.meta_title || slug,
        content_ids: [slug],
        value: cfg.price || 89000,
        currency: "IDR",
        num_items: 1,
      });
    }
  }, [showCheckout, cfg, slug]);

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nama dan email wajib diisi");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/promo/buy", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        promo_slug: slug,
      });
      setResult(data);
      // Meta Pixel: Purchase event — critical for ROAS optimization
      fbqTrack("Purchase", {
        content_name: cfg?.meta_title || slug,
        content_category: "promo",
        content_ids: [slug],
        value: cfg?.price || 89000,
        currency: "IDR",
      });
      fbqTrack("Lead", {
        content_name: cfg?.meta_title || slug,
        content_category: "promo",
        content_ids: [slug],
        value: cfg?.price || 89000,
        currency: "IDR",
      });
      if (data.redirect_url) window.location.href = data.redirect_url;
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBuying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1E]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  if (!cfg)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0F1E] gap-4">
        <p className="text-gray-400 text-lg">Halaman tidak ditemukan.</p>
        <Link to="/" className="text-blue-400 hover:underline">
          Kembali
        </Link>
      </div>
    );

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1E] p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full grid place-items-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-gray-900 mb-2">
            Akun Dibuat! 🎉
          </h2>
          <p className="text-gray-500 mb-6">
            Selesaikan pembayaran, lalu login:
          </p>
          <div className="bg-gray-50 rounded-2xl p-4 text-left space-y-2 mb-4">
            <div>
              <span className="text-xs text-gray-400">Email</span>
              <p className="font-mono font-semibold">{result.email}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400">Password</span>
              <p className="font-mono font-black text-lg text-blue-600 bg-blue-50 px-3 py-2 rounded-xl select-all flex items-center justify-between">
                {result.generated_password}
                <button
                  type="button"
                  onClick={copyPassword}
                  className="ml-2 shrink-0 p-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 transition text-blue-600"
                  title="Salin password"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </p>
            </div>
          </div>
          <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-4 py-2">
            ⚠️ Simpan password ini!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1E] text-white">
      {/* Sticky urgency bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-700 to-cyan-600 text-white px-4 py-2.5 text-center text-sm font-bold tracking-wide flex items-center justify-center gap-2 flex-wrap">
        <CountdownTimer slug={slug} urgencyText={cfg.urgency_text} />
        <span className="hidden sm:inline">•</span>
        <span className="opacity-90">Sisa {scarcity} kuota</span>
      </div>

      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-blue-900/20">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Hero — navy + blue, uppercase */}
      <section className="pt-20 pb-16 px-4 relative overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Blue glow effects */}
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl pointer-events-none" />
          
          <span className="inline-block bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-black uppercase tracking-[0.2em] px-4 py-2 rounded-sm mb-6">
            {cfg.hero_badge}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight">
            <span className="text-white">{cfg.headline.split("\n")[0]}</span>
            <br />
            <span className="text-blue-400">
              {cfg.headline.split("\n")[1] || ""}
            </span>
          </h1>
          <p className="text-gray-400 mt-6 text-lg max-w-2xl mx-auto leading-relaxed">
            {cfg.subheadline}
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="text-gray-600 line-through text-2xl font-bold">
              {rupiah(cfg.original_price)}
            </span>
            <span className="font-display text-5xl sm:text-6xl font-black text-blue-400">
              {rupiah(cfg.price)}
            </span>
          </div>
          
          {/* Breakdown yang didapat */}
          <ul className="space-y-2 mb-6 mt-6 text-sm text-white/70 max-w-md mx-auto text-left">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              8.000 kredit scraping (= 8.000 leads bisnis)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Akses semua fitur Pro selama 30 hari
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              WA Blast unlimited (pakai Fonnte kamu sendiri)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Email Blast via Gmail kamu sendiri
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Garansi refund 7 hari — tidak perlu alasan
            </li>
          </ul>
          
          <button
            onClick={() => setShowCheckout(true)}
            className="mt-2 inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-4 text-lg font-black uppercase tracking-wide hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
          >
            <Zap className="w-6 h-6" fill="white" />
            {cfg.cta_text}
          </button>
          <p className="text-center mt-3">
            <a href="https://wa.me/6282342302512" 
               target="_blank"
               rel="noopener noreferrer"
               className="text-white/40 text-xs hover:text-white/70 transition underline underline-offset-2">
              Ada pertanyaan? Chat WhatsApp dulu →
            </a>
          </p>
          <p className="text-xs text-gray-600 mt-2">
            Pembayaran via iPaymu — VA, QRIS, E-Wallet
          </p>
        </div>
      </section>

      {/* Problem scenario — the vivid daily scenario */}
      {cfg.problem_scenario && (
        <section className="py-16 px-4 border-t border-blue-900/30">
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-300 text-lg leading-relaxed italic border-l-4 border-blue-500 pl-6">
              {cfg.problem_scenario}
            </p>
          </div>
        </section>
      )}

      {/* Pain points — numbered, brutal honesty */}
      <section className="py-16 px-4 border-t border-blue-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-10">
            <span className="text-blue-400">Masalahnya:</span> Kamu sudah tau
          </h2>
          <div className="space-y-6">
            {cfg.pain_points.map((p, i) => (
              <div
                key={i}
                className="flex gap-5 items-start p-6 bg-white/5 border border-blue-900/30 rounded-xl"
              >
                <span className="text-3xl font-black text-blue-400 shrink-0 w-12">
                  {p.icon}
                </span>
                <div>
                  <h3 className="font-display font-bold text-xl text-white mb-1">
                    {p.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Income Potential — the "reveal" moment */}
      {cfg.potential_title && (
        <section className="py-20 px-4 border-t border-blue-900/30 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-yellow-500 to-amber-600 rounded-full" />
              <div className="pl-8">
                <span className="inline-block bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1 rounded-sm mb-4">
                  PELUANG YANG KAMU LEWATKAN
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-6 leading-tight">
                  {cfg.potential_title}
                </h2>
                <div className="text-gray-300 text-lg leading-relaxed whitespace-pre-line mb-8">
                  {cfg.potential_text}
                </div>
                {cfg.potential_points?.length > 0 && (
                  <div className="space-y-4">
                    {cfg.potential_points.map((point, i) => (
                      <div key={i} className="flex items-start gap-4 p-5 bg-yellow-500/5 border border-yellow-500/15 rounded-xl">
                        <div className="shrink-0 w-8 h-8 bg-yellow-500/10 rounded-full grid place-items-center">
                          <span className="text-yellow-400 font-black text-sm">{i + 1}</span>
                        </div>
                        <span className="text-gray-200 font-medium leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-8 flex items-center gap-3 bg-white/5 border border-blue-900/30 rounded-xl px-5 py-3 max-w-fit">
                  <span className="text-3xl font-black text-yellow-400">=</span>
                  <p className="text-white font-bold text-lg">
                    Leads siap pakai + Platform otomatis = Income baru
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Solution — clean, no-fluff */}
      <section className="py-16 px-4 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-8 text-white">
            {cfg.solution_title}
          </h2>
          <div className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">
            {cfg.solution_text}
          </div>
        </div>
      </section>

      {/* Screenshot Showcase — tabbed */}
      <section className="py-16 px-4 border-t border-blue-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1 rounded-sm mb-3">
              RAHASIA PARA AGENCY
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white">
              Lihat Sendiri—
              <span className="text-blue-400">Ini Tampilan Aslinya</span>
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              Screenshot ASLI dari dashboard RESADA. Bukan mockup. Bukan hasil
              edit.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white/5 border border-blue-900/30 rounded-xl p-1 mb-6 overflow-x-auto">
            {[
              { id: "ss1", icon: MapPin, label: "Scraper Maps" },
              { id: "ss2", icon: MapPin, label: "Hasil Scrape" },
              { id: "ss3", icon: MapPin, label: "Detail Lead" },
              { id: "ss4", icon: BookOpen, label: "Community Tips" },
              { id: "ssfb1", icon: Facebook, label: "Scraper FB" },
              { id: "ssig1", icon: Instagram, label: "Scraper IG" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wide whitespace-nowrap transition ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-lg"
                    : "text-gray-500 hover:text-white hover:bg-white/5"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <button
              type="button"
              onClick={() => setFullscreenImage(activeTab)}
              className="group relative rounded-xl overflow-hidden border border-blue-900/30 bg-white/5 text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-blue-500/70"
            >
              <img
                src={`/image/sda/demo/${activeTab}.webp`}
                alt={activeTab}
                className="w-full h-auto transition duration-300 group-hover:scale-[1.015]"
              />
              <span className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-[#0A0F1E]/70 text-white opacity-0 shadow-lg transition group-hover:opacity-100 group-focus:opacity-100">
                <Maximize2 className="h-5 w-5" />
              </span>
            </button>

            <div className="space-y-4">
              <span className="inline-block bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1 rounded-sm">
                {activeTabData?.badge || "FITUR"}
              </span>
              <h3 className="font-display text-2xl font-black text-white">
                {activeTabData?.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {activeTabData?.desc}
              </p>
              <ul className="space-y-2">
                {activeTabData?.benefits?.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-300"
                  >
                    <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Live Demo — WebM + lazy load */}
      <section className="py-16 px-4 border-t border-blue-900/30 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1 rounded-sm mb-3">
            LIVE DEMO
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-3">
            Lihat RESADA <span className="text-blue-400">Langsung Beraksi</span>
          </h2>
          <p className="text-gray-500 mb-8 max-w-2xl mx-auto">
            Klik play untuk lihat sendiri cara kerja RESADA dari awal sampai
            blast. Bukan slideshow. Bukan animasi. Ini rekaman asli.
          </p>

          {!videoLoaded && !videoUrl ? (
            <button
              onClick={loadVideo}
              disabled={videoLoading}
              className="group relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden border border-blue-900/30 bg-gradient-to-br from-gray-900 to-[#0A0F1E] aspect-video flex items-center justify-center cursor-pointer hover:border-blue-500/40 transition-all duration-300"
            >
              {/* Thumbnail overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="relative flex flex-col items-center gap-4">
                {videoLoading ? (
                  <>
                    <svg
                      className="animate-spin h-10 w-10 text-blue-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <p className="text-sm text-gray-400 font-semibold">
                      Mendownload video demo…
                    </p>
                    <div className="w-48 sm:w-64 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">{videoProgress}%</p>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-blue-600 group-hover:bg-blue-500 flex items-center justify-center transition-colors shadow-2xl shadow-blue-600/30">
                      <svg
                        className="w-8 h-8 text-white ml-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-400 font-semibold tracking-wide">
                      Klik untuk tonton demo
                    </p>
                    <p className="text-[11px] text-gray-600">
                      ~2.5MB — mulai download saat kamu klik
                    </p>
                  </>
                )}
              </div>
            </button>
          ) : (
            <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden border border-blue-900/30 bg-[#0A0F1E] shadow-2xl">
              <video
                ref={videoRef}
                className="w-full aspect-video"
                controls
                autoPlay
                playsInline
                poster="/image/sda/demo/ss1.webp"
              >
                {videoUrl && <source src={videoUrl} type="video/mp4" />}
                Browser kamu tidak mendukung video.
              </video>
            </div>
          )}

          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-green-500" /> 1 menit 23 detik
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-green-500" /> Asli, bukan edit
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-green-500" /> MP4 ~2.5MB
            </span>
          </div>
        </div>
      </section>

      {/* YouTube Demo */}
      {cfg.youtube_id && (
        <section className="py-16 px-4 border-t border-blue-900/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-8">
              Lihat Beraksi 🔥
            </h2>
            <div className="relative rounded-xl overflow-hidden aspect-video bg-gray-900 border border-white/10">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${cfg.youtube_id}?rel=0`}
                title="RESADA Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Features — checkmarks, clean */}
      <section className="py-16 px-4 border-t border-blue-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-10">
            Yang Kamu Dapat
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cfg.features.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-green-500/10 border border-green-500/20 p-5 rounded-xl"
              >
                <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <span className="text-gray-200 font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-scroll CTA — after Features */}
      <section className="py-12 px-4 border-t border-blue-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-white/5 border border-blue-500/30 rounded-2xl p-8">
            <p className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-2">Udah lihat fiturnya. Sekarang action.</p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-gray-600 line-through text-lg">{rupiah(cfg.original_price)}</span>
              <span className="font-display text-4xl font-black text-blue-400">{rupiah(cfg.price)}</span>
            </div>
            
            {/* Breakdown yang didapat */}
            <ul className="space-y-2 mb-6 text-sm text-white/70 max-w-md mx-auto text-left">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                8.000 kredit scraping (= 8.000 leads bisnis)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Akses semua fitur Pro selama 30 hari
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                WA Blast unlimited (pakai Fonnte kamu sendiri)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Email Blast via Gmail kamu sendiri
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Garansi refund 7 hari — tidak perlu alasan
              </li>
            </ul>
            
            <button
              onClick={() => setShowCheckout(true)}
              className="inline-flex items-center gap-3 bg-blue-600 text-white px-10 py-4 text-lg font-black uppercase tracking-wide hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
            >
              <Zap className="w-5 h-5" fill="white" /> {cfg.cta_text}
            </button>
            <p className="text-center mt-3">
              <a href="https://wa.me/6282342302512" 
                 target="_blank"
                 rel="noopener noreferrer"
                 className="text-white/40 text-xs hover:text-white/70 transition underline underline-offset-2">
                Ada pertanyaan? Chat WhatsApp dulu →
              </a>
            </p>
            <p className="text-xs text-gray-500 mt-2">Tersisa {scarcity} kuota &bull; Garansi 7 hari refund</p>
          </div>
        </div>
      </section>

      {/* Testimonials — 3 testimonials grid */}
      <section className="py-16 px-4 border-t border-blue-900/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-10 text-center text-white">
            Kata Mereka
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-[#111827] border border-blue-900/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500 grid place-items-center text-white font-bold text-sm shrink-0">
                  AR
                </div>
                <div>
                  <p className="font-bold text-white text-sm">AR</p>
                  <p className="text-white/50 text-xs">Freelance Web Developer &bull; Solo</p>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-white/80 text-sm italic leading-relaxed">
                "Biasanya habis 2-3 jam cari nomor manual di Maps. Sekarang 10 menit udah dapet 300+ kontak lengkap. Lumayan banget buat outreach harian."
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-[#111827] border border-blue-900/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500 grid place-items-center text-white font-bold text-sm shrink-0">
                  DK
                </div>
                <div>
                  <p className="font-bold text-white text-sm">DK</p>
                  <p className="text-white/50 text-xs">Digital Agency Owner &bull; Jakarta</p>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-white/80 text-sm italic leading-relaxed">
                "Coba scrape 'salon Jakarta Selatan', keluar 400+ data dalam beberapa menit. Langsung blast WA pakai template. Yang reply sekitar 30-an. Worth it banget harganya."
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-[#111827] border border-blue-900/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-500 grid place-items-center text-white font-bold text-sm shrink-0">
                  BW
                </div>
                <div>
                  <p className="font-bold text-white text-sm">BW</p>
                  <p className="text-white/50 text-xs">Web Dev Freelancer &bull; Surabaya</p>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-white/80 text-sm italic leading-relaxed">
                "Awalnya skeptis. Tapi coba dulu, ternyata datanya lengkap — ada nomor WA, website, rating semua ada. Sekarang jadi tools wajib tiap cari klien baru."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk reversal */}
      {cfg.risk_reversal && (
        <section className="py-12 px-4 border-t border-blue-900/30">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl px-6 py-4">
              <Shield className="w-6 h-6 text-green-400" />
              <span className="text-green-300 font-bold text-lg">
                {cfg.risk_reversal}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* FAQ — objection handling */}
      <section className="py-16 px-4 border-t border-blue-900/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-8 text-center">
            Pertanyaan yang Sering Muncul
          </h2>
          <div className="space-y-3">
            {[
              { q: "Nomor WhatsApp saya aman gak?", a: "Aman. RESADA pakai sistem BYOK (Bring Your Own Key) — pesan dikirim dari akun Fonnte kamu sendiri, bukan dari gateway kami. Rate limiting otomatis menjaga nomor kamu tetap aman dari blokir." },
              { q: "Beda apa sama paket biasa di resada.id?", a: "Paket Agency normal Rp599.000/tahun. Lewat promo ini kamu dapat akses 1 tahun dengan harga Rp89.000 — diskon 85%. Fitur sama persis: 8.000 kredit/bulan, semua sumber, WA & Email blast." },
              { q: "Gimana kalau gak cocok?", a: "Garansi refund 100% dalam 7 hari pertama. Tanpa syarat. Kalau gak ada leads masuk, uang kembali. Kami serius — no questions asked." },
              { q: "Butuh skill coding?", a: "Gak sama sekali. Tinggal ketik keyword + pilih lokasi, klik Mulai. Semua otomatis. UI-nya dirancang untuk orang non-teknis." },
              { q: "Berapa leads yang bisa didapat per hari?", a: "Paket Agency: 8.000 kredit/bulan. Rata-rata user dapat 100+ leads relevan dalam hari pertama. Tergantung niche dan lokasi yang kamu target." },
              { q: "Pembayaran aman?", a: "100% aman. Lewat iPaymu (payment gateway resmi OJK). Support VA, QRIS, GoPay, OVO, Dana. Kami tidak menyimpan data kartu kredit kamu." },
            ].map((faq, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left bg-white/5 border border-blue-900/30 rounded-xl p-5 hover:bg-white/[0.07] transition"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </div>
                {faqOpen === i && (
                  <p className="text-gray-400 mt-3 leading-relaxed text-sm">{faq.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof — buyer counter */}
      <section className="py-10 px-4 border-t border-blue-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 bg-white/5 border border-blue-900/30 rounded-full px-6 py-3">
            <div className="flex -space-x-2">
              {["bg-blue-500","bg-blue-500","bg-green-500","bg-yellow-500","bg-purple-500"].map((c,i) => (
                <div key={i} className={`w-8 h-8 rounded-full ${c} border-2 border-[#0A0F1E] grid place-items-center text-[10px] font-bold text-white`}>
                  {["RA","SD","BS","MP","AP"][i]}
                </div>
              ))}
            </div>
            <span className="text-sm text-gray-300">Dipakai di <span className="font-bold text-white">15+ kota</span> di Indonesia</span>
          </div>
        </div>
      </section>

      {/* Final CTA — gradient blue background */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900 to-[#0A0F1E] border border-blue-800/50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mb-4 text-white">
            Jangan Sampai Kehabisan
          </h2>
          <p className="text-lg text-white/80 mb-6">
            Setelah kuota habis, harga naik ke {rupiah(cfg.original_price)}.
            Tanpa nego.
          </p>
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-white/40 line-through text-2xl font-bold">
              {rupiah(cfg.original_price)}
            </span>
            <span className="font-display text-5xl font-black text-white">
              {rupiah(cfg.price)}
            </span>
          </div>
          
          {/* Breakdown yang didapat */}
          <ul className="space-y-2 mb-6 text-sm text-white/90 max-w-md mx-auto text-left">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              8.000 kredit scraping (= 8.000 leads bisnis)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Akses semua fitur Pro selama 30 hari
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              WA Blast unlimited (pakai Fonnte kamu sendiri)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Email Blast via Gmail kamu sendiri
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400 shrink-0" />
              Garansi refund 7 hari — tidak perlu alasan
            </li>
          </ul>
          
          <button
            onClick={() => setShowCheckout(true)}
            className="inline-flex items-center gap-3 bg-white text-blue-700 px-10 py-4 text-lg font-black uppercase tracking-wide hover:bg-gray-100 transition-colors shadow-lg shadow-blue-500/30"
          >
            <Zap className="w-6 h-6" />
            {cfg.cta_text}
          </button>
          <p className="text-center mt-3">
            <a href="https://wa.me/6282342302512" 
               target="_blank"
               rel="noopener noreferrer"
               className="text-white/60 text-xs hover:text-white/90 transition underline underline-offset-2">
              Ada pertanyaan? Chat WhatsApp dulu →
            </a>
          </p>
          <p className="text-sm text-white/50 mt-2">Tersisa {scarcity} kuota</p>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-8 px-4 bg-[#0D1117] border-t border-blue-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-blue-600/20 grid place-items-center">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-xs text-gray-400 font-semibold">Pembayaran Aman</p>
              <p className="text-[10px] text-gray-600">SSL Encrypted</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-blue-600/20 grid place-items-center">
                <Check className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-xs text-gray-400 font-semibold">Semua Metode</p>
              <p className="text-[10px] text-gray-600">VA, QRIS, E-Wallet</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-blue-600/20 grid place-items-center">
                <ArrowRight className="w-6 h-6 text-blue-400 rotate-180" />
              </div>
              <p className="text-xs text-gray-400 font-semibold">Garansi 7 Hari</p>
              <p className="text-[10px] text-gray-600">No Questions Asked</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-blue-600/20 grid place-items-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-xs text-gray-400 font-semibold">15+ Kota</p>
              <p className="text-[10px] text-gray-600">Di Indonesia</p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Community — Join Group */}
      <section className="py-16 px-4 bg-gradient-to-br from-green-900/20 to-[#0A0F1E] border-t border-green-800/30">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block bg-green-600/20 border border-green-500/30 text-green-400 text-xs font-black uppercase tracking-[0.2em] px-4 py-2 rounded-sm mb-6">
            💬 EKSKLUSIF UNTUK MEMBER
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-4">
            Gabung Komunitas WhatsApp RESADA
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Tips closing, strategi sales, update fitur terbaru, dan networking dengan sesama pengguna RESADA. 
            <span className="text-green-400 font-semibold"> Eksklusif member only.</span>
          </p>
          <a
            href="https://chat.whatsapp.com/DpyvduAVV8iArmknAqMviV?mode=gi_t"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-600 text-white px-8 py-4 text-lg font-black uppercase tracking-wide hover:bg-green-700 transition-all shadow-lg shadow-green-600/30 hover:shadow-green-600/50 active:scale-95"
          >
            <MessageCircle className="w-6 h-6" />
            Gabung Komunitas Sekarang
          </a>
          <p className="text-xs text-green-400/60 mt-4">
            ✓ Gratis untuk semua pengguna RESADA
          </p>
        </div>
      </section>

      {/* Footer — with legal links */}
      <footer className="bg-[#0A0F1E] text-gray-600 text-center py-6 text-sm border-t border-blue-900/30">
        <p>&copy; 2026 RESADA — Revenue Engine & Sales Automation</p>
        <div className="flex items-center justify-center gap-4 mt-2 text-xs">
          <a href="https://resada.id/privacy" className="hover:text-gray-400 transition">Kebijakan Privasi</a>
          <a href="https://resada.id/terms" className="hover:text-gray-400 transition">Syarat &amp; Ketentuan</a>
          <a href="https://resada.id/refund" className="hover:text-gray-400 transition">Kebijakan Refund</a>
          <a href="https://resada.id" className="text-blue-400 hover:underline">resada.id</a>
        </div>
      </footer>


      {/* Exit-intent popup */}
      {showExitIntent && !result && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#0A0F1E]/80 backdrop-blur-sm" onClick={() => setShowExitIntent(false)} />
          <div className="relative bg-white text-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
            <button onClick={() => setShowExitIntent(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <div className="w-14 h-14 bg-blue-100 rounded-xl grid place-items-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="font-display text-2xl font-extrabold mb-2">Tunggu Dulu!</h3>
            <p className="text-gray-600 text-sm mb-4">
              Promo ini terbatas. Setelah kuota habis, harga naik ke <span className="line-through">{rupiah(cfg.original_price)}</span>.
            </p>
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="text-blue-600 font-bold text-sm">Harga promo HANYA {rupiah(cfg.price)}</p>
              <p className="text-xs text-gray-500 mt-1">Sisa {scarcity} kuota &bull; Garansi 7 hari refund</p>
            </div>
            <button
              onClick={() => { setShowExitIntent(false); setShowCheckout(true); }}
              className="w-full bg-blue-600 text-white py-4 rounded-xl font-black text-lg uppercase tracking-wide hover:bg-blue-700 transition"
            >
              Klaim Harga Promo
            </button>
            <button onClick={() => setShowExitIntent(false)} className="mt-3 text-xs text-gray-400 hover:text-gray-600">
              Lewati, saya mau harga normal
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen image */}
      {fullscreenImage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0F1E]/95 p-4 sm:p-8">
          <button
            type="button"
            aria-label="Tutup fullscreen"
            onClick={() => setFullscreenImage(null)}
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/70"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            type="button"
            aria-label="Tutup gambar fullscreen"
            onClick={() => setFullscreenImage(null)}
            className="absolute inset-0 cursor-zoom-out"
          />
          <img
            src={`/image/sda/demo/${fullscreenImage}.webp`}
            alt={fullscreenImage}
            className="relative z-10 max-h-[90vh] max-w-[96vw] rounded-xl border border-blue-900/30 object-contain shadow-2xl"
          />
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-[#0A0F1E]/80 backdrop-blur-sm"
            onClick={() => setShowCheckout(false)}
          />
          <div className="relative bg-white text-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-8">
            <button
              onClick={() => setShowCheckout(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-blue-600 rounded-xl grid place-items-center mx-auto mb-3">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display text-2xl font-extrabold">Checkout</h3>
              <div className="flex items-center justify-center gap-3 mt-2">
                <span className="text-gray-400 line-through">
                  {rupiah(cfg.original_price)}
                </span>
                <span className="font-display text-3xl font-extrabold text-blue-600">
                  {rupiah(cfg.price)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Agency Plan — 1 tahun
              </p>
            </div>
            <form onSubmit={handleBuy} className="space-y-4">
              <div>
                <label className="text-sm font-bold text-gray-700">
                  Nama *
                </label>
                <input
                  required
                  type="text"
                  placeholder="Nama kamu"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700">
                  Email *
                </label>
                <input
                  required
                  type="email"
                  placeholder="kamu@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-gray-700">
                  No. WhatsApp <span className="text-gray-400">(opsional)</span>
                </label>
                <input
                  type="tel"
                  placeholder="082342302512"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={buying}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-black text-lg uppercase tracking-wide hover:bg-blue-700 transition disabled:opacity-60"
              >
                {buying ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />{" "}
                    Memproses...
                  </>
                ) : (
                  `Bayar ${rupiah(cfg.price)}`
                )}
              </button>
              <div className="flex items-center justify-center gap-4 text-xs text-gray-400 pt-2">
                <span className="flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Aman
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-3 h-3" /> Password auto-generate
                </span>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Social Proof Notification */}
      {showSocialProof && currentProof && (
        <div className="fixed bottom-20 md:bottom-6 left-4 z-40 transition-all duration-500 ease-out animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-white text-gray-900 rounded-xl shadow-2xl p-4 flex items-center gap-3 max-w-xs border border-gray-200">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 grid place-items-center shrink-0">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {currentProof.name}
              </p>
              <p className="text-xs text-gray-600 truncate">
                {currentProof.email} • {currentProof.city}
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Baru saja membeli • {currentProof.time} lalu
              </p>
            </div>
            <button 
              onClick={() => setShowSocialProof(false)}
              className="text-gray-400 hover:text-gray-600 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Sticky Bottom Bar for Mobile */}
      <StickyBottomBar onBuy={() => setShowCheckout(true)} />
    </div>
  );
}
